// Command layer. Every mutation of unit intent -- from the human player's mouse
// or from the AI -- funnels through applyCommand(). Commands are validated here
// (ownership, tech, cost, supply, placement) and turned into orders on entities.
//
// COMMAND SHAPE  { type, player, ids:[], x, y, targetId, defId, queued, slot }

import { UnitDefs, BuildingDefs, ResearchDefs, TRAINER_OF, techRequirements } from './defs.js';
import { MAX_SUPPLY } from './constants.js';

export const GROUND_ORDERS = new Set(['move', 'attackmove', 'attack', 'patrol', 'gather', 'build', 'repair']);

/**
 * Guard against non-finite destinations.
 *
 * A command carrying undefined or NaN coordinates does not fail loudly -- it
 * produces an order the movement system integrates into the unit's position,
 * which turns x/y into NaN. The unit then still exists and is still "alive",
 * but it is nowhere: it cannot be drawn, cannot be selected, cannot be hit, and
 * silently poisons anything that averages over positions. It is lost for the
 * rest of the match. Reject the order instead.
 */
const finitePoint = (x, y) => Number.isFinite(x) && Number.isFinite(y);

export function canAfford(player, cost) {
  return player.minerals >= (cost.minerals || 0) && player.gas >= (cost.gas || 0);
}
export function pay(player, cost) {
  player.minerals -= cost.minerals || 0;
  player.gas -= cost.gas || 0;
}
export function refund(player, cost) {
  player.minerals += cost.minerals || 0;
  player.gas += cost.gas || 0;
}

/** True if `player` has every structure needed to make `defId`. */
export function techMet(world, player, defId) {
  for (const req of techRequirements(defId)) if (!world.hasBuilding(player, req)) return false;
  if (UnitDefs[defId]) {
    const t = TRAINER_OF[defId];
    if (t && !world.hasBuilding(player, t)) return false;
  }
  return true;
}

/** Human-readable reason a build/train is unavailable, or null if it is fine. */
export function unavailableReason(world, player, defId) {
  const def = UnitDefs[defId] || BuildingDefs[defId] || ResearchDefs[defId];
  if (!def) return 'unknown';
  const missing = techRequirements(defId).filter((r) => !world.hasBuilding(player, r));
  if (UnitDefs[defId]) { const t = TRAINER_OF[defId]; if (t && !world.hasBuilding(player, t) && !missing.includes(t)) missing.push(t); }
  if (missing.length) return `Requires ${BuildingDefs[missing[0]].name}`;
  const p = world.players[player];
  if (!canAfford(p, def.cost)) return p.minerals < (def.cost.minerals || 0) ? 'Not enough minerals' : 'Not enough vespene gas';
  if (UnitDefs[defId] && p.supplyUsed + UnitDefs[defId].supply > Math.min(MAX_SUPPLY, p.supplyMax)) return 'Additional supply required';
  return null;
}

export function setOrder(e, order, queued) {
  // A gather order is a STANDING order -- it never calls nextOrder(), so
  // anything shift-queued behind it waits forever. Shift-queueing onto a mining
  // worker (the build chain the design calls for) must therefore take effect
  // now; economy.js sends the worker back to the mineral line when it is done.
  if (queued && e.order && e.order.type === 'gather' && e.orderQueue.length === 0) queued = false;
  if (queued && e.order) { e.orderQueue.push(order); return; }
  e.order = order;
  e.orderQueue.length = 0;
  e.path = null;
  e.pathGoal = null;
  e.stuckTicks = 0;
  e.target = null;
  if (e.kind === 'unit' && order && order.type !== 'gather') {
    // dropping a gather order abandons the node reservation
    e.gatherTimer = 0;
  }
}

export function nextOrder(e) {
  e.target = null;
  e.path = null;
  e.pathGoal = null;
  e.stuckTicks = 0;
  e.order = e.orderQueue.length ? e.orderQueue.shift() : null;
  return e.order;
}

const isCommandable = (world, e, player) =>
  e && e.alive && e.owner === player && (e.kind === 'unit' || e.kind === 'building');

export function applyCommand(world, cmd) {
  const player = world.players[cmd.player];
  if (!player || player.defeated) return;
  const ents = (cmd.ids || []).map((id) => world.get(id)).filter((e) => isCommandable(world, e, cmd.player));
  const q = !!cmd.queued;

  switch (cmd.type) {
    case 'move':
    case 'attackmove':
    case 'patrol': {
      if (!finitePoint(cmd.x, cmd.y)) return;
      for (const e of ents) {
        if (e.kind !== 'unit') continue;
        if (e.sieged) continue;                       // sieged tanks must unsiege first
        const o = { type: cmd.type, x: cmd.x, y: cmd.y };
        if (cmd.type === 'patrol') { o.fromX = e.x; o.fromY = e.y; o.leg = 0; }
        setOrder(e, o, q);
      }
      break;
    }
    case 'attack': {
      const tgt = world.get(cmd.targetId);
      for (const e of ents) {
        if (e.kind !== 'unit') continue;
        // Stamp where the target was when the order was given. combat.js only
        // refreshes lastX/lastY on ticks where it can SEE the target, so an
        // attack ordered on something that slips into fog would otherwise be
        // dropped instantly and the unit would just stand there.
        if (tgt && tgt.alive) { setOrder(e, { type: 'attack', targetId: tgt.id, lastX: tgt.x, lastY: tgt.y }, q); continue; }
        // Target already gone. Attack-move to the click point if we were given
        // one -- the input layer sends targetId ALONE, so there may be none,
        // and inventing an attack-move to (undefined, undefined) is how a unit
        // ends up at NaN. With no point to go to, the order is simply dropped.
        if (finitePoint(cmd.x, cmd.y)) setOrder(e, { type: 'attackmove', x: cmd.x, y: cmd.y }, q);
        else if (!q) { setOrder(e, null, false); e.vx = 0; e.vy = 0; }
      }
      break;
    }
    case 'stop': {
      for (const e of ents) {
        // Stop on a production building clears its rally point, as in BW.
        if (e.kind === 'building') { e.rally = null; continue; }
        setOrder(e, null, false);
        e.vx = 0; e.vy = 0;
        // Releasing the harvest slot is what makes Stop actually stick on a
        // worker. economy.js puts an idle worker straight back on its patch if
        // it still remembers one, so without this the order is undone one tick
        // later and S reads as a broken hotkey. Node counts are reconciled from
        // scratch each tick, so clearing the reference here is sufficient.
        e.gatherNode = null;
        e.gatherTimer = 0;
      }
      break;
    }
    case 'hold': {
      for (const e of ents) { if (e.kind === 'unit') { setOrder(e, { type: 'hold', x: e.x, y: e.y }, q); e.vx = 0; e.vy = 0; } }
      break;
    }
    case 'gather': {
      const node = world.get(cmd.targetId);
      for (const e of ents) {
        if (e.kind !== 'unit' || !UnitDefs[e.defId].isWorker) continue;
        setOrder(e, { type: 'gather', targetId: node ? node.id : null }, q);
      }
      break;
    }
    case 'repair': {
      const tgt = world.get(cmd.targetId);
      if (!tgt) break;
      for (const e of ents) if (e.kind === 'unit' && UnitDefs[e.defId].isWorker) setOrder(e, { type: 'repair', targetId: tgt.id }, q);
      break;
    }
    case 'build': {
      const def = BuildingDefs[cmd.defId];
      if (!def) break;
      const worker = ents.find((e) => e.kind === 'unit' && UnitDefs[e.defId].canBuild);
      if (!worker) break;
      if (unavailableReason(world, cmd.player, cmd.defId)) { world.emit({ type: 'error', player: cmd.player, msg: unavailableReason(world, cmd.player, cmd.defId) }); break; }
      const spot = validatePlacement(world, cmd.player, cmd.defId, cmd.x, cmd.y);
      if (!spot.ok) { world.emit({ type: 'error', player: cmd.player, msg: spot.reason }); break; }
      pay(player, def.cost);
      setOrder(worker, { type: 'build', defId: cmd.defId, tx: spot.tx, ty: spot.ty, geyserId: spot.geyserId, paid: true }, q);
      break;
    }
    case 'train': {
      const def = UnitDefs[cmd.defId];
      if (!def) break;
      const b = ents.find((e) => e.kind === 'building' && !e.constructing && BuildingDefs[e.defId].produces.includes(cmd.defId))
             || pickTrainer(world, cmd.player, cmd.defId);
      if (!b) break;
      const reason = unavailableReason(world, cmd.player, cmd.defId);
      if (reason) { world.emit({ type: 'error', player: cmd.player, msg: reason }); break; }
      if (b.queue.length >= 5) { world.emit({ type: 'error', player: cmd.player, msg: 'Queue is full' }); break; }
      pay(player, def.cost);
      b.queue.push({ kind: 'unit', defId: cmd.defId, timeLeft: def.buildTime, total: def.buildTime });
      break;
    }
    case 'research': {
      const def = ResearchDefs[cmd.defId];
      if (!def) break;
      if (player.tech.has(cmd.defId)) break;
      const b = ents.find((e) => e.kind === 'building' && !e.constructing && BuildingDefs[e.defId].research.includes(cmd.defId));
      if (!b) break;
      if (b.queue.some((x) => x.kind === 'research')) break;
      if (!canAfford(player, def.cost)) { world.emit({ type: 'error', player: cmd.player, msg: 'Not enough resources' }); break; }
      pay(player, def.cost);
      b.queue.push({ kind: 'research', defId: cmd.defId, timeLeft: def.researchTime, total: def.researchTime });
      break;
    }
    case 'rally': {
      for (const e of ents) {
        if (e.kind !== 'building') continue;
        e.rally = cmd.targetId != null ? { targetId: cmd.targetId, x: cmd.x, y: cmd.y } : { x: cmd.x, y: cmd.y, targetId: null };
      }
      break;
    }
    case 'siege':
    case 'unsiege': {
      for (const e of ents) {
        if (e.kind !== 'unit' || !UnitDefs[e.defId].canSiege) continue;
        if (!world.players[cmd.player].tech.has('siege_tech')) { world.emit({ type: 'error', player: cmd.player, msg: 'Requires Siege Tech' }); break; }
        const want = cmd.type === 'siege';
        if (e.sieged === want || e.siegeTimer > 0) continue;
        e.siegeTimer = UnitDefs[e.defId].siegeTransition;
        e.siegePending = want;
        setOrder(e, null, false);
      }
      break;
    }
    case 'cloak':
    case 'decloak': {
      for (const e of ents) {
        if (e.kind !== 'unit' || !UnitDefs[e.defId].canCloak) continue;
        if (!world.players[cmd.player].tech.has('cloaking_field')) break;
        e.cloaked = cmd.type === 'cloak';
      }
      break;
    }
    case 'cancel': {
      for (const e of ents) {
        if (e.kind === 'building' && e.queue.length) {
          const slot = cmd.slot != null ? Math.min(cmd.slot, e.queue.length - 1) : e.queue.length - 1;
          const item = e.queue.splice(slot, 1)[0];
          const def = item.kind === 'unit' ? UnitDefs[item.defId] : ResearchDefs[item.defId];
          refund(player, def.cost);
        } else if (e.kind === 'building' && e.constructing) {
          // A cancelled foundation is not a combat loss: route it through an
          // explicit removal rather than hp = -1, which would emit a 'death'
          // event and inflate the player's units-lost stat.
          refund(player, BuildingDefs[e.defId].cost);
          e.cancelled = true;
          world.emit({ type: 'cancelled', id: e.id, owner: e.owner, defId: e.defId, x: e.x, y: e.y });
          world.removeEntity(e.id);
        }
      }
      break;
    }
    default: break;
  }
}

function pickTrainer(world, player, unitId) {
  const want = TRAINER_OF[unitId];
  let best = null;
  for (const b of world.buildingsOf(player)) {
    if (b.constructing || b.defId !== want) continue;
    if (!best || b.queue.length < best.queue.length) best = b;
  }
  return best;
}

/**
 * Snap a build request to a tile anchor and check legality.
 * Returns { ok, tx, ty, geyserId?, reason? }.
 */
export function validatePlacement(world, player, defId, wx, wy) {
  const def = BuildingDefs[defId];
  const size = def.size;
  if (def.onGeyser) {
    // snap to the nearest un-refined geyser within a few tiles
    let best = null, bd = Infinity;
    for (const id of world.resources) {
      const r = world.get(id);
      if (!r || r.defId !== 'geyser' || r.refineryId) continue;
      const d = Math.hypot(r.x - wx, r.y - wy);
      if (d < bd) { bd = d; best = r; }
    }
    if (!best || bd > 6) return { ok: false, reason: 'Must be placed on a vespene geyser' };
    return { ok: true, tx: best.tx, ty: best.ty, geyserId: best.id };
  }
  const tx = Math.round(wx - size / 2);
  const ty = Math.round(wy - size / 2);
  if (!world.grid.canPlace(tx, ty, size, size)) return { ok: false, reason: 'Cannot build there' };
  return { ok: true, tx, ty, geyserId: null };
}
